import { useState } from 'react'

import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'

import { ButtonLink } from 'components/Buttons'
import { components } from 'components/DefaultMdxComponents'
import { Heading } from 'components/Heading'
import { useApiContext } from 'components/OpenApi/context'
import { DeprecationContextProvider, useDeprecationContext } from 'components/OpenApi/Deprecation'
import { getResponseRefValue } from 'components/OpenApi/getRefValue'
import { Section } from 'components/Section'

import { Attribute } from '../../Attribute'
import { List, Wrapper } from '../../Common'
import { Example } from '../../Example'
import { Payload } from '../Payload'
import { PropertyTitle } from '../PropertyTitle'
import { StatusCode } from '../StatusCode'
import { TitleWrapper } from '../TitleWrapper'
import { Empty } from './Empty'

type Props = {
	linkPrefix: string

	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	response: OpenApi3.ReferenceObject | OpenApi3.ResponseObject
	statusCode: string

	stickyOffset?: number
}

export const Response = ({ linkPrefix, response, statusCode, definitions, stickyOffset }: Props) => {
	const { components: apiComponents } = useApiContext()
	const resolvedResponse =
		'$ref' in response
			? getResponseRefValue({
					responses: apiComponents.responses,
					ref: response.$ref,
					description: response.description,
				})
			: response
	const contentTypes = Object.keys(resolvedResponse?.content ?? {})
	const [selectedContentType, setSelectedContentType] = useState(contentTypes[0])
	const deprecationContext = useDeprecationContext()

	if (!resolvedResponse) return null

	const selectedContent = resolvedResponse.content ? resolvedResponse.content[selectedContentType] : null

	return (
		<DeprecationContextProvider value={deprecationContext}>
			<Wrapper>
				<Section
					title={
						<>
							<StatusCode statusCode={Number.parseInt(statusCode, 10)} /> Response
						</>
					}
					renderHeading={({ level, id, label, children }) => (
						<TitleWrapper>
							<Heading level={level} id={id} label={label}>
								{children}
							</Heading>
							{deprecationContext.hasDeprecated && (
								<ButtonLink
									label={
										deprecationContext.showDeprecated ? 'Hide deprecated properties' : 'Show deprecated properties'
									}
									slim
									onClick={deprecationContext.toggleShowDeprecated}
								/>
							)}
						</TitleWrapper>
					)}
					label={`${statusCode}-response`}
					prefix={linkPrefix}
					level={3}
				>
					{resolvedResponse.description && (
						<ReactMarkdown components={components}>{resolvedResponse.description}</ReactMarkdown>
					)}
					{resolvedResponse.headers && (
						<Section
							title='Headers'
							prefix={`${linkPrefix}-${statusCode}-response`}
							level={4}
							renderHeading={(props) => <PropertyTitle {...props} />}
						>
							<List>
								{Object.entries(resolvedResponse.headers).map(([key, value], index) => (
									<Attribute
										key={index}
										name={key}
										value={
											'schema' in value && value.schema
												? {
														...value.schema,
														description: value.description,
														example: value.example,
													}
												: value
										}
										linkPrefix={`${linkPrefix}-${statusCode}-response-headers`}
										isExpandedInitially={Object.keys(resolvedResponse.headers!).length === 1}
										isRequired
									/>
								))}
							</List>
						</Section>
					)}
					<Section
						title='Payload'
						prefix={`${linkPrefix}-${statusCode}-response`}
						level={4}
						renderHeading={(props) => <PropertyTitle {...props} />}
					>
						{selectedContent ? (
							<Payload data={selectedContent} linkPrefix={`${linkPrefix}-${statusCode}-response-payload`} />
						) : (
							<Empty>No payload for this response</Empty>
						)}
					</Section>
				</Section>
				<Example
					stickyOffset={stickyOffset}
					title={`${statusCode} response`}
					data={resolvedResponse}
					definitions={definitions}
					selectedContentType={selectedContentType}
					setSelectedContentType={setSelectedContentType}
				/>
			</Wrapper>
		</DeprecationContextProvider>
	)
}
